import React, {
    useState, useContext, useEffect, useCallback, useMemo
} from 'react';
import { TranslatorContext } from '@jutro/locale';
import { BreakpointTrackerContext } from '@jutro/layout';
import { sanitizeUrl } from '@jutro/platform';
import { ViewModelForm, ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import { SgProductService, SgSessionStorageService, SgQuickQuoteService } from 'saga-modules';
import CommonConstants from 'sg-common-constants';
import { SgDate, SgDateInputTypes } from 'sg-date';
import { LoadSaveService } from 'gw-capability-quoteandbind';
import { useValidation } from 'gw-portals-validation-react';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import { Link } from '@jutro/components';
import _ from 'lodash';
import metadata from './SgTimeoutRetrievalForm.metadata.json5';
import messages from '../SgCommonErrorMessages.messages';

function SgSessionTimeoutError(props) {
    const {
        effectiveDate,
        quoteID,
        location,
        history,
        phoneNumber
    } = props;

    const { onValidate, isComponentValid } = useValidation('timeoutErrorPage');
    const translator = useContext(TranslatorContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const isMobile = breakpoint === 'tablet' || breakpoint === 'phoneWide' || breakpoint === 'phone';
    // guidewire workaround (taken from wizard.jsx), model updates does not re-render page otherwise
    const [toggle, flip] = useState(false);
    const [retrieveQuoteVM, updateRetrieveQuoteVM] = useState(undefined);
    const [isPageInitialized, setIsPageInitialized] = useState(false);
    const [showErrors, setShowError] = useState(false);
    const [showRetrievalError, setShowRetrievalError] = useState(false);
    const [newFullQuoteHref, setNewFullQuoteHref] = useState('#/journey');
    const viewModelService = useContext(ViewModelServiceContext);

    const {
        isHome,
        isMotor,
        isQuickQuote,
        isSmi
    } = appConfig;

    useEffect(() => {
        if (isQuickQuote) {
            const productCode = SgProductService.isQuickQuoteHome() ? CommonConstants.HOME_PRODUCT_CODE : CommonConstants.MOTOR_PRODUCT_CODE;
            SgQuickQuoteService.getQuickToFullQuoteLink(productCode).then((result) => {
                setNewFullQuoteHref(result);
            });
        }
    }, []);

    useEffect(() => {
        if (!viewModelService) {
            return;
        }
        const dtoData = {
            insuranceType_Ext: SgProductService.getProductCode(),
            effectiveDate: effectiveDate
        };
        if (SgProductService.isMotorHome()) {
            dtoData.isMotorHome = SgProductService.isMotorHome();
        }

        const retrieveQuoteViewModel = viewModelService.create(
            dtoData,
            'pc',
            'edgev11.capabilities.quote.submission.dto.QuoteRetrievalDTO'
        );
        updateRetrieveQuoteVM(retrieveQuoteViewModel);
        setIsPageInitialized(true);
    }, [setIsPageInitialized, viewModelService, isHome, effectiveDate, quoteID]);

    const defaqtoLogoImage = useMemo(() => {
        if (isHome || SgProductService.isQuickQuoteHome()) {
            return 'sgDefaqtoLogoHome';
        }
        if (isMotor || isQuickQuote) {
            return 'sgDefaqtoLogoMotor';
        }
        if (isSmi) {
            return '';
        }
        return '';
    }, [isHome, isMotor, isSmi]);

    const updateData = useCallback((data) => {
        updateRetrieveQuoteVM(data);
        flip(!toggle);
    }, [toggle, retrieveQuoteVM, updateRetrieveQuoteVM]);

    const resumeQuote = useCallback(() => {
        if (!isComponentValid) {
            setShowError(true);
            return;
        }
        _.set(retrieveQuoteVM, 'quoteID', quoteID);
        LoadSaveService.retrieveSubmission(retrieveQuoteVM.value).then((resp) => {
            const wizardLink = '/journey';
            if (_.isEmpty(resp)) {
                // empty response means submission wasn't found due to incorrect data; throwing error to get correct message displayed
                const err = { appErrorCode: CommonConstants.SG_ERROR_CODES.GW_ENTITY_NOT_FOUND_ERROR };
                throw err;
            }
            SgSessionStorageService.setItem(SgSessionStorageService.KEY.LAST_URL, location.pathname);
            SgSessionStorageService.setItem(SgSessionStorageService.KEY.MODEL, resp);
            SgSessionStorageService.setItem(SgSessionStorageService.KEY.IS_RETRIEVAL, true);
            history.push(wizardLink, { submission: resp });
        }).catch(() => {
            setShowRetrievalError(true);
        });
    }, [retrieveQuoteVM, isComponentValid, location, history, setShowRetrievalError]);

    const newQuoteHref = useMemo(() => {
        let result = '/journey';
        if (isQuickQuote) {
            const id = SgSessionStorageService.getItem(SgSessionStorageService.KEY.QQ_BASE_ID);
            const productCode = isHome || SgProductService.isQuickQuoteHome()
                ? CommonConstants.HOME_PRODUCT_CODE : CommonConstants.MOTOR_PRODUCT_CODE;
            result = `/new-quickQuote?id=${id}&product=${productCode}`;
        }
        return result;
    }, []);

    if (!isPageInitialized) {
        return '';
    }

    const overrideProps = {
        dateOfBirth: {
            path: 'dateOfBirth',
            customId: 'dateOfBirth',
            onValidate: onValidate,
            showErrors: showErrors,
            modelVM: retrieveQuoteVM,
            updateData: updateData,
            datePickerTypeDesktop: SgDateInputTypes.DAY_MONTH_YEAR_INPUTS,
            autoCompleteYear: true
        },
        retrievalError: {
            visible: showRetrievalError,
            content: (
                <>
                    {translator(messages.TimeoutResumeError)}
                    <Link id="timeoutResumeLink" to={newQuoteHref}>{translator(messages.TimeoutResumeErrorLink)}</Link>
                </>
            )
        }
    };

    const resolvers = {
        resolveComponentMap: {
            SgDate: SgDate
        },
        resolveCallbackMap: {
            onResumeQuote: resumeQuote
        }
    };

    return (
        <div>
            <div>
                <p className="sgErrorPagePrimaryText">{translator(messages.TimeoutMessage1)}</p>
                <p className="sgErrorPageSecondaryText">{translator(messages.TimeoutMessage2)}</p>
                <div>
                    <ViewModelForm
                        uiProps={metadata.componentContent}
                        model={retrieveQuoteVM}
                        overrideProps={overrideProps}
                        onModelChange={updateData}
                        onValidationChange={onValidate}
                        showErrors={showErrors}
                        callbackMap={resolvers.resolveCallbackMap}
                        componentMap={resolvers.resolveComponentMap}
                    />
                </div>
                <div className="sgErrorPageSecondaryText sgErrorPageBottomText">
                    <span>{translator(messages.TimeoutAlternatively1)}</span>
                    <Link id="timeoutStartNewQuote" href={sanitizeUrl(newFullQuoteHref)}>{translator(messages.TimeoutAlternatively2)}</Link>
                    <span>
                        <span>
                            {translator(messages.TimeoutAlternatively3, {
                                phoneNumber: isMobile
                                    ? <a href={`tel:${phoneNumber.replaceAll(' ', '')}`}>{phoneNumber}</a>
                                    : <strong>{phoneNumber}</strong>
                            })}
                        </span>
                    </span>
                </div>
            </div>
            <div className={defaqtoLogoImage} id="defaqtoLogo" />
        </div>
    );
}

export default SgSessionTimeoutError;
